/** Pure draft store for the record editors (a booking with its guest and party; orders).
 *
 * Generalises `features/tour-operators/config-batch/changeSet.ts`. Entries are keyed
 * `"<entity>:<id>"` → path, where the path is a catalog field key or `action:<key>` for
 * a queued action, and each holds its ORIGINAL baseline (`from`) and the value the draft
 * wants (`to`). Editing back to the baseline deletes the entry. Every user action pushes
 * ONE inverse history entry, which may span several paths (a tour move that also clears a
 * dependent value); a new action clears redo; Discard all is undoable. Text inputs
 * coalesce per focus session through a token on SET.
 *
 * REBASE takes fresh stored values — a 409's `drifted[]`, or the fresh edit_context
 * baselines after a restore or a refetch — and marks every entry whose baseline moved
 * `stale` (Keep mine / Take theirs resolve it), unless the fresh value carries an
 * automatic merge (`mergedTo`, an id list both sides changed). Both history stacks clear
 * when a rebase outdates the inverses they hold (the config-batch REBASE_TENANT
 * precedent).
 *
 * Record ids may be negative: a row the draft creates (`alias:-1`) until it is saved; the
 * save's SAVED moves what the draft still holds of it onto its real id (`created`). A
 * temporary id is issued once per editing session (`tempIdFloor`): an id a save in flight
 * sent — or an undo took out of the entries — is never handed to another row, so a save's
 * `created` can only name the row it was sent for.
 *
 * `compileChangeset` turns the draft into the request body: one final value per field
 * with its `from`; the client never sends an operation log. The body is the generic
 * `RecordChangeset` — each record kind's API narrows it to its endpoint's wire type. No
 * React here — `DraftProvider.tsx` is the wrapper.
 */

/** `"<entity>:<id>"`, e.g. `booking:4711`, `user:9001`. */
export type RecordKey = string;

export const ACTION_PREFIX = 'action:';

export function recordKey(entity: string, id: number): RecordKey {
  return `${entity}:${id}`;
}

export function parseRecordKey(rec: RecordKey): { entity: string; id: number } {
  const i = rec.lastIndexOf(':');
  return { entity: rec.slice(0, i), id: Number(rec.slice(i + 1)) };
}

/** The draft path of a queued action. */
export function actionPath(action: string): string {
  return `${ACTION_PREFIX}${action}`;
}

export function isActionPath(path: string): boolean {
  return path.startsWith(ACTION_PREFIX);
}

/** The server's key for one change, `"<entity>:<id>:<field or action>"` — what 400
 *  `errors`, preview outcomes and drifted fields are keyed by. */
export function changeKey(rec: RecordKey, path: string): string {
  return `${rec}:${isActionPath(path) ? path.slice(ACTION_PREFIX.length) : path}`;
}

/** Who last changed a stored value (BookingRecordChangedBy). */
export interface ChangedBy {
  label: string;
  ts?: string | null;
  source?: string | null;
}

/** One entry of a 409's `drifted[]`: a change whose stored value moved under it — the
 *  structural core of every record kind's drifted field. */
export interface RecordDriftedField {
  /** `"<entity>:<id>:<field or action>"`. */
  key: string;
  entity: string;
  id: number;
  field?: string | null;
  action?: string | null;
  /** The stored value now (wire format). */
  current: unknown;
  changed_by?: ChangedBy | null;
  /** The record is gone on the server (a row deleted elsewhere): drop the change. */
  gone?: boolean;
}

export interface StaleInfo {
  /** The stored value now. */
  current: unknown;
  changedBy: ChangedBy | null;
  /** The record itself is gone on the server (a row deleted elsewhere): the change can
   *  only be dropped — Keep mine has nothing to keep it on. */
  gone?: true;
}

export interface DraftEntry {
  /** The baseline the change was made from — sent as the change's `from`. */
  from: unknown;
  to: unknown;
  /** Set by REBASE when the stored value moved away from `from`. */
  stale?: StaleInfo;
}

export interface HistoryItem {
  rec: RecordKey;
  path: string;
  /** Entry before the action (undefined = no entry). */
  prev: DraftEntry | undefined;
  /** Entry after the action (undefined = no entry). */
  next: DraftEntry | undefined;
}

export type HistoryKind = 'edit' | 'discard' | 'keep_mine' | 'take_theirs';

export interface HistoryEntry {
  kind: HistoryKind;
  items: HistoryItem[];
  /** Caller-supplied description ("Move to Bath & Stonehenge"); else derived. */
  label?: string;
  /** Focus-session token of a coalesced text edit. */
  coalesce?: string;
}

export interface DraftState {
  /** RecordKey → path → entry. Empty records are removed rather than kept as `{}`. */
  entries: Record<RecordKey, Record<string, DraftEntry>>;
  undoStack: HistoryEntry[];
  redoStack: HistoryEntry[];
  /** Increments on every state change — the memo signal for selector hooks. */
  version: number;
  /** The lowest temporary id the editing session has issued (0: none) — a high-water mark
   *  that only goes down: a SET lowers it to the ids it issues (`issued`) and the negative
   *  record ids it writes, and nothing else moves it (undo, redo, a rebase, a save keep it)
   *  until RESET or another record's draft. `nextTempId` goes below it, so a temporary id is
   *  never issued twice — not even once the entries no longer hold it (a new row removed, or
   *  undone, while a save that sent it is in flight). */
  tempIdFloor: number;
}

export const initialDraftState: DraftState = {
  entries: {},
  undoStack: [],
  redoStack: [],
  version: 0,
  tempIdFloor: 0,
};

export interface DraftChange {
  rec: RecordKey;
  path: string;
  /** The current baseline; ignored when an entry exists (it keeps its original). */
  from: unknown;
  to: unknown;
}

export interface DraftTarget {
  rec: RecordKey;
  path: string;
}

/** One change a save stored: where, and the value it sent. */
export interface SavedChange extends DraftTarget {
  to: unknown;
}

/** The rows a save created (a `SAVED`'s `created`): each temporary record key → the real id
 *  the server gave it (`{"day:-1": 9123}`), and the record kind's membership-list paths
 *  (`membershipPaths`), whose id lists name those rows too. */
export interface SavedCreated {
  ids: Readonly<Record<RecordKey, number>>;
  memberships: ReadonlySet<string>;
}

export interface FreshValue {
  rec: RecordKey;
  path: string;
  current: unknown;
  changedBy?: ChangedBy | null;
  /** An automatic merge of the entry onto `current` (an id list the server and the draft
   *  both changed — `mergeIdList`): the entry takes `current` as its new `from` and this
   *  as its `to` instead of turning stale. Equal to `current` = the change is obsolete. */
  mergedTo?: unknown;
  /** The record is gone on the server (a tombstone): the entry turns stale and can only be
   *  dropped. */
  gone?: boolean;
}

export type DraftAction =
  /** One user action — one history entry, however many paths. With `coalesce`, a SET
   *  repeating the previous SET's token and paths folds into its history entry. `issued`:
   *  the temporary ids the action hands out (a new row's, `nextTempId`) — the session
   *  never issues them again (`tempIdFloor`), whatever happens to the entries after. */
  | { type: 'SET'; changes: DraftChange[]; label?: string; coalesce?: string; issued?: readonly number[] }
  /** Back to baseline (↺). On a stale entry this shows the stored value — Take theirs. */
  | { type: 'REVERT'; targets: DraftTarget[]; label?: string }
  /** Stale entry: adopt the stored value as the new `from`, keep `to`. Refused for an
   *  entry whose record is gone (`stale.gone`). */
  | { type: 'KEEP_MINE'; target: DraftTarget }
  /** Stale entry: drop it — the stored value wins. */
  | { type: 'TAKE_THEIRS'; target: DraftTarget }
  | { type: 'DISCARD_ALL' }
  | { type: 'REBASE'; fresh: FreshValue[] }
  /** Not undoable: drop entries and history (after a save — the changes are stored). */
  | { type: 'RESET' }
  /** Not undoable, after a save: drop the entries the save stored — each one still
   *  holding the value it sent — and history. An entry edited while the save was in
   *  flight stays, re-based onto the value the save stored; entries the save did not
   *  carry stay as they are. With `created`, what stays of the rows the save created moves
   *  onto their real ids in the same step — never an undo step of its own, so no undo can
   *  bring a temporary id back once the server has created the row. */
  | { type: 'SAVED'; sent: SavedChange[]; created?: SavedCreated }
  | { type: 'UNDO' }
  | { type: 'REDO' };

// ---------------------------------------------------------------------------
// Equality
// ---------------------------------------------------------------------------

/** Value equality for wire values: deep, with `undefined`, `null` and `""` all meaning
 *  empty at the top level (the wire rule for text fields; no other widget has a meaningful
 *  `""`). Objects compare by their keys whatever their order (a list-valued field's rows
 *  built by the client against the server's), arrays in order. */
export function draftValuesEqual(a: unknown, b: unknown): boolean {
  const na = a === undefined || a === '' ? null : a;
  const nb = b === undefined || b === '' ? null : b;
  if (na === nb) return true;
  if (na === null || nb === null || typeof na !== 'object' || typeof nb !== 'object') {
    return false;
  }
  return deepEqual(na, nb);
}

function deepEqual(a: unknown, b: unknown): boolean {
  if (a === b) return true;
  if (typeof a !== 'object' || typeof b !== 'object' || a === null || b === null) return false;
  if (Array.isArray(a) || Array.isArray(b)) {
    if (!Array.isArray(a) || !Array.isArray(b) || a.length !== b.length) return false;
    return a.every((x, i) => deepEqual(x, b[i]));
  }
  const x = a as Record<string, unknown>;
  const y = b as Record<string, unknown>;
  // An absent key and an undefined one are the same (JSON drops both).
  const keys = new Set([...Object.keys(x), ...Object.keys(y)].filter((k) => x[k] !== undefined || y[k] !== undefined));
  for (const k of keys) if (!deepEqual(x[k], y[k])) return false;
  return true;
}

function changedByEqual(a: ChangedBy | null | undefined, b: ChangedBy | null | undefined) {
  return JSON.stringify(a ?? null) === JSON.stringify(b ?? null);
}

function entriesEqual(a: DraftEntry | undefined, b: DraftEntry | undefined): boolean {
  if (a === undefined || b === undefined) return a === b;
  if (!draftValuesEqual(a.from, b.from) || !draftValuesEqual(a.to, b.to)) return false;
  if (!a.stale || !b.stale) return !a.stale && !b.stale;
  return (
    draftValuesEqual(a.stale.current, b.stale.current) &&
    changedByEqual(a.stale.changedBy, b.stale.changedBy) &&
    Boolean(a.stale.gone) === Boolean(b.stale.gone)
  );
}

/** What the field shows when the entry is gone: the stored value. */
function valueWithoutEntry(entry: DraftEntry): unknown {
  return entry.stale ? entry.stale.current : entry.from;
}

// ---------------------------------------------------------------------------
// Reducer
// ---------------------------------------------------------------------------

type EntriesMap = DraftState['entries'];

/** Immutably set/delete one entry, dropping empty records. */
function withEntry(
  entries: EntriesMap,
  rec: RecordKey,
  path: string,
  entry: DraftEntry | undefined,
): EntriesMap {
  const nextRec = { ...(entries[rec] ?? {}) };
  if (entry === undefined) delete nextRec[path];
  else nextRec[path] = entry;
  const next = { ...entries };
  if (Object.keys(nextRec).length === 0) delete next[rec];
  else next[rec] = nextRec;
  return next;
}

function applyItems(entries: EntriesMap, items: HistoryItem[], side: 'prev' | 'next'): EntriesMap {
  let out = entries;
  for (const item of items) out = withEntry(out, item.rec, item.path, item[side]);
  return out;
}

/** Apply `entry.items` (their `next` side), push it, clear redo, bump the version. */
function pushHistory(state: DraftState, entry: HistoryEntry): DraftState {
  return {
    ...state,
    entries: applyItems(state.entries, entry.items, 'next'),
    undoStack: [...state.undoStack, entry],
    redoStack: [],
    version: state.version + 1,
  };
}

/** The lowest negative integer of `ids` and `floor` (a temporary id is a negative integer). */
function lowestTempId(floor: number, ids: Iterable<unknown>): number {
  let min = floor;
  for (const id of ids) if (typeof id === 'number' && Number.isInteger(id) && id < min) min = id;
  return min;
}

/** The floor after a SET: the ids it issues, and the negative record ids it writes. */
function floorAfterSet(floor: number, action: Extract<DraftAction, { type: 'SET' }>): number {
  return lowestTempId(lowestTempId(floor, action.issued ?? []), action.changes.map((c) => parseRecordKey(c.rec).id));
}

/** A draft restored from its stored copy (the session's first state): its entries, and as
 *  the floor the lowest of the stored session's own floor (`storedFloor`: the ids it issued,
 *  those it freed since included) and the temporary ids the entries hold — every negative
 *  record id, and every negative integer of a list value (the copy does not say which lists
 *  are membership lists; counting them all can only lower the next id, never repeat one). */
export function restoredDraftState(entries: DraftState['entries'], storedFloor = 0): DraftState {
  let floor = lowestTempId(0, [storedFloor]);
  for (const [rec, recEntries] of Object.entries(entries)) {
    floor = lowestTempId(floor, [parseRecordKey(rec).id]);
    for (const entry of Object.values(recEntries)) {
      for (const v of [entry.from, entry.to]) if (Array.isArray(v)) floor = lowestTempId(floor, v);
    }
  }
  return { ...initialDraftState, entries, tempIdFloor: floor };
}

function sortedRecordKeys(entries: EntriesMap): RecordKey[] {
  return Object.keys(entries).sort(compareRecordKeys);
}

/** Entity name, then numeric id — `booking:9` before `booking:10` before `user:1`. */
function compareRecordKeys(a: RecordKey, b: RecordKey): number {
  const pa = parseRecordKey(a);
  const pb = parseRecordKey(b);
  if (pa.entity !== pb.entity) return pa.entity < pb.entity ? -1 : 1;
  return pa.id - pb.id;
}

/** The entry a SET leaves behind. An existing entry keeps its ORIGINAL baseline; a stale
 *  one stays stale (only Keep mine / Take theirs resolve it) unless the user typed the
 *  stored value itself, which is agreeing with the server. */
function entryAfterSet(
  existing: DraftEntry | undefined,
  change: DraftChange,
): DraftEntry | undefined {
  if (existing?.stale) {
    if (draftValuesEqual(change.to, existing.stale.current)) return undefined;
    return { from: existing.from, to: change.to, stale: existing.stale };
  }
  const from = existing ? existing.from : change.from;
  if (draftValuesEqual(change.to, from)) return undefined;
  return { from, to: change.to };
}

function sameTargets(a: HistoryItem[], b: HistoryItem[]): boolean {
  if (a.length !== b.length) return false;
  const keys = new Set(a.map((i) => `${i.rec}|${i.path}`));
  return b.every((i) => keys.has(`${i.rec}|${i.path}`));
}

function reduceSet(state: DraftState, action: Extract<DraftAction, { type: 'SET' }>): DraftState {
  const items: HistoryItem[] = [];
  let entries = state.entries;
  for (const change of action.changes) {
    const existing = entries[change.rec]?.[change.path];
    const next = entryAfterSet(existing, change);
    const same = items.find((i) => i.rec === change.rec && i.path === change.path);
    if (same) same.next = next;
    else items.push({ rec: change.rec, path: change.path, prev: existing, next });
    entries = withEntry(entries, change.rec, change.path, next);
  }
  const effective = items.filter((i) => !entriesEqual(i.prev, i.next));
  if (effective.length === 0) return state;
  const tempIdFloor = floorAfterSet(state.tempIdFloor, action);

  const top = state.undoStack[state.undoStack.length - 1];
  if (action.coalesce && top?.coalesce === action.coalesce && sameTargets(top.items, effective)) {
    // Same focus session, same field(s): fold into the session's history entry.
    const merged = top.items.map((t) => ({
      ...t,
      next: effective.find((i) => i.rec === t.rec && i.path === t.path)!.next,
    }));
    const below = state.undoStack.slice(0, -1);
    const netNoop = merged.every((m) => entriesEqual(m.prev, m.next));
    return {
      entries,
      undoStack: netNoop ? below : [...below, { ...top, items: merged }],
      redoStack: [],
      version: state.version + 1,
      tempIdFloor,
    };
  }
  return pushHistory({ ...state, tempIdFloor }, {
    kind: 'edit',
    items: effective,
    ...(action.label ? { label: action.label } : {}),
    ...(action.coalesce ? { coalesce: action.coalesce } : {}),
  });
}

/** Does `entry` still agree with the stored value `current`? */
function entryMatchesStored(entry: DraftEntry, current: unknown): boolean {
  return (
    draftValuesEqual(entry.from, current) ||
    (entry.stale !== undefined && draftValuesEqual(entry.stale.current, current))
  );
}

function reduceRebase(state: DraftState, fresh: FreshValue[]): DraftState {
  let entries = state.entries;
  let changed = false;
  const byTarget = new Map<string, FreshValue>();
  for (const f of fresh) {
    byTarget.set(`${f.rec}|${f.path}`, f);
    const entry = entries[f.rec]?.[f.path];
    if (!entry) continue;
    let next: DraftEntry | undefined;
    if (draftValuesEqual(f.current, entry.to)) {
      // The server already holds the wanted value — the change is obsolete.
      next = undefined;
    } else if (draftValuesEqual(f.current, entry.from)) {
      next = { from: entry.from, to: entry.to };
    } else if (f.mergedTo !== undefined) {
      // Both sides changed an id list: the merge keeps both — never stale.
      next = draftValuesEqual(f.mergedTo, f.current) ? undefined : { from: f.current, to: f.mergedTo };
    } else {
      const changedBy = f.changedBy !== undefined ? f.changedBy : (entry.stale?.changedBy ?? null);
      next = { from: entry.from, to: entry.to, stale: { current: f.current, changedBy, ...(f.gone ? { gone: true as const } : {}) } };
    }
    if (!entriesEqual(entry, next)) {
      entries = withEntry(entries, f.rec, f.path, next);
      changed = true;
    }
  }
  // Inverses recorded against a baseline that moved are outdated (an undo would bring
  // back the old `from`) — then both stacks go.
  const outdated = [...state.undoStack, ...state.redoStack].some((h) =>
    h.items.some((item) => {
      const f = byTarget.get(`${item.rec}|${item.path}`);
      if (!f) return false;
      return [item.prev, item.next].some(
        (e) => e !== undefined && !entryMatchesStored(e, f.current),
      );
    }),
  );
  if (!changed && !outdated) return state;
  return {
    ...state,
    entries,
    undoStack: outdated ? [] : state.undoStack,
    redoStack: outdated ? [] : state.redoStack,
    version: state.version + 1,
  };
}

/** What the draft still holds of the rows a save created — edits made while it was in flight
 *  — moved onto their real ids: a temporary record's entries onto its real record (which
 *  holds nothing yet: it did not exist), and every temporary id in a membership list, its
 *  `from` and its `to`, swapped for the real one. A kind's temporary ids are unique across
 *  its entities (`nextTempId`), so a list's ids need no entity. */
function rekeyCreated(entries: EntriesMap, created: SavedCreated): EntriesMap {
  const recs = new Map<RecordKey, RecordKey>();
  const ids = new Map<number, number>();
  for (const [temp, real] of Object.entries(created.ids)) {
    const { entity, id } = parseRecordKey(temp);
    if (!(id < 0)) continue; // only a temporary id is re-keyed
    recs.set(temp, recordKey(entity, real));
    ids.set(id, real);
  }
  if (ids.size === 0) return entries;
  const swap = (v: unknown) =>
    Array.isArray(v) ? v.map((x: unknown) => (typeof x === 'number' ? (ids.get(x) ?? x) : x)) : v;
  let out: EntriesMap = {};
  for (const [rec, recEntries] of Object.entries(entries)) {
    for (const [path, entry] of Object.entries(recEntries)) {
      const next = created.memberships.has(path) ? { ...entry, from: swap(entry.from), to: swap(entry.to) } : entry;
      out = withEntry(out, recs.get(rec) ?? rec, path, next);
    }
  }
  return out;
}

function reduceSaved(state: DraftState, sent: SavedChange[], created?: SavedCreated): DraftState {
  const stored = new Map(sent.map((c) => [`${c.rec}|${c.path}`, c.to]));
  let entries: EntriesMap = {};
  for (const [rec, recEntries] of Object.entries(state.entries)) {
    for (const [path, entry] of Object.entries(recEntries)) {
      const key = `${rec}|${path}`;
      if (!stored.has(key)) {
        entries = withEntry(entries, rec, path, entry); // not part of this save
        continue;
      }
      const savedTo = stored.get(key);
      if (draftValuesEqual(entry.to, savedTo)) continue; // stored as it stands
      // Edited while the save was in flight: a change against what is stored now.
      entries = withEntry(entries, rec, path, { from: savedTo, to: entry.to });
    }
  }
  // After the drop (the save sent the temporary keys), before history goes.
  if (created) entries = rekeyCreated(entries, created);
  const empty =
    Object.keys(state.entries).length === 0 && state.undoStack.length === 0 && state.redoStack.length === 0;
  if (empty) return state;
  // The floor stays: the ids the save created are the server's now, never to be reissued.
  return { ...state, entries, undoStack: [], redoStack: [], version: state.version + 1 };
}

export function draftReducer(state: DraftState, action: DraftAction): DraftState {
  switch (action.type) {
    case 'SET':
      return reduceSet(state, action);
    case 'REVERT': {
      const items: HistoryItem[] = [];
      for (const t of action.targets) {
        const existing = state.entries[t.rec]?.[t.path];
        if (existing && !items.some((i) => i.rec === t.rec && i.path === t.path)) {
          items.push({ rec: t.rec, path: t.path, prev: existing, next: undefined });
        }
      }
      if (items.length === 0) return state;
      return pushHistory(state, {
        kind: 'edit',
        items,
        ...(action.label ? { label: action.label } : {}),
      });
    }
    case 'KEEP_MINE': {
      const { rec, path } = action.target;
      const existing = state.entries[rec]?.[path];
      if (!existing?.stale || existing.stale.gone) return state;
      const next: DraftEntry = { from: existing.stale.current, to: existing.to };
      return pushHistory(state, {
        kind: 'keep_mine',
        items: [{ rec, path, prev: existing, next }],
      });
    }
    case 'TAKE_THEIRS': {
      const { rec, path } = action.target;
      const existing = state.entries[rec]?.[path];
      if (!existing?.stale) return state;
      return pushHistory(state, {
        kind: 'take_theirs',
        items: [{ rec, path, prev: existing, next: undefined }],
      });
    }
    case 'DISCARD_ALL': {
      const items: HistoryItem[] = [];
      for (const rec of sortedRecordKeys(state.entries)) {
        const recEntries = state.entries[rec] ?? {};
        for (const path of Object.keys(recEntries).sort()) {
          items.push({ rec, path, prev: recEntries[path], next: undefined });
        }
      }
      if (items.length === 0) return state;
      return pushHistory(state, { kind: 'discard', items });
    }
    case 'REBASE':
      return reduceRebase(state, action.fresh);
    case 'SAVED':
      return reduceSaved(state, action.sent, action.created);
    case 'RESET': {
      const empty =
        Object.keys(state.entries).length === 0 &&
        state.undoStack.length === 0 &&
        state.redoStack.length === 0 &&
        state.tempIdFloor === 0;
      if (empty) return state;
      // A new session: its temporary ids start again.
      return { entries: {}, undoStack: [], redoStack: [], version: state.version + 1, tempIdFloor: 0 };
    }
    case 'UNDO': {
      const entry = state.undoStack[state.undoStack.length - 1];
      if (!entry) return state;
      return {
        ...state,
        entries: applyItems(state.entries, entry.items, 'prev'),
        undoStack: state.undoStack.slice(0, -1),
        redoStack: [...state.redoStack, entry],
        version: state.version + 1,
      };
    }
    case 'REDO': {
      const entry = state.redoStack[state.redoStack.length - 1];
      if (!entry) return state;
      return {
        ...state,
        entries: applyItems(state.entries, entry.items, 'next'),
        undoStack: [...state.undoStack, entry],
        redoStack: state.redoStack.slice(0, -1),
        version: state.version + 1,
      };
    }
  }
}

// ---------------------------------------------------------------------------
// Fresh values
// ---------------------------------------------------------------------------

/** Path-space baselines: edit_context `baselines` (field keys) plus an `action:<key>` path
 *  per queued action, read from the field it changes (`checkin` → `checkin_status`). */
export function pathBaselines(
  baselines: Record<RecordKey, Record<string, unknown>>,
  actionFields: Record<string, string> = {},
): Record<RecordKey, Record<string, unknown>> {
  const out: Record<RecordKey, Record<string, unknown>> = {};
  for (const [rec, fields] of Object.entries(baselines)) {
    const rows: Record<string, unknown> = { ...fields };
    for (const [action, field] of Object.entries(actionFields)) {
      if (field in fields) rows[actionPath(action)] = fields[field];
    }
    out[rec] = rows;
  }
  return out;
}

/** Every value of path-space baselines as REBASE input. */
export function freshFromBaselines(
  baselines: Record<RecordKey, Record<string, unknown>>,
): FreshValue[] {
  const out: FreshValue[] = [];
  for (const [rec, fields] of Object.entries(baselines)) {
    for (const [path, current] of Object.entries(fields)) out.push({ rec, path, current });
  }
  return out;
}

/** A 409's `drifted[]` as REBASE input (an `action` item maps to its `action:` path). */
export function freshFromDrift(
  drifted: ReadonlyArray<Omit<RecordDriftedField, 'key'>>,
): FreshValue[] {
  return drifted.flatMap((d) => {
    const path = d.action ? actionPath(d.action) : d.field;
    if (!path) return [];
    return [
      {
        rec: recordKey(d.entity, d.id),
        path,
        current: d.current,
        changedBy: d.changed_by ?? null,
        ...(d.gone ? { gone: true } : {}),
      },
    ];
  });
}

/** How many entries are stale, and how many of them are on a record that is gone. */
export function staleCounts(state: DraftState): { stale: number; gone: number } {
  let stale = 0;
  let gone = 0;
  for (const recEntries of Object.values(state.entries)) {
    for (const entry of Object.values(recEntries)) {
      if (!entry.stale) continue;
      stale += 1;
      if (entry.stale.gone) gone += 1;
    }
  }
  return { stale, gone };
}

// ---------------------------------------------------------------------------
// Selectors — pure functions over DraftState
// ---------------------------------------------------------------------------

export function draftEntry(
  state: DraftState,
  rec: RecordKey,
  path: string,
): DraftEntry | undefined {
  return state.entries[rec]?.[path];
}

export function draftCount(state: DraftState): number {
  let n = 0;
  for (const recEntries of Object.values(state.entries)) n += Object.keys(recEntries).length;
  return n;
}

export function hasStale(state: DraftState): boolean {
  for (const recEntries of Object.values(state.entries)) {
    for (const entry of Object.values(recEntries)) if (entry.stale) return true;
  }
  return false;
}

export interface DraftRecordGroup {
  rec: RecordKey;
  entity: string;
  id: number;
  entries: Array<{ path: string; entry: DraftEntry }>;
}

/** Entries grouped per record for the ledger: the records in `order` first (e.g. the
 *  booking, its guest, then the party), then any others by entity and id; paths sorted. */
export function groupByRecord(state: DraftState, order: RecordKey[] = []): DraftRecordGroup[] {
  const rank = new Map(order.map((rec, i) => [rec, i]));
  const recs = Object.keys(state.entries).sort((a, b) => {
    const ra = rank.get(a);
    const rb = rank.get(b);
    if (ra !== undefined && rb !== undefined) return ra - rb;
    if (ra !== undefined) return -1;
    if (rb !== undefined) return 1;
    return compareRecordKeys(a, b);
  });
  return recs.map((rec) => {
    const recEntries = state.entries[rec] ?? {};
    return {
      rec,
      ...parseRecordKey(rec),
      entries: Object.keys(recEntries)
        .sort()
        .map((path) => ({ path, entry: recEntries[path]! })),
    };
  });
}

/** Entries per stage, for the spine's dirty dots. `stageOf` is a path → stage map
 *  (`date` → departure, `action:checkin` → onsite) or a function of the record and path;
 *  paths it does not place are not counted. */
export function countByStage<S extends string>(
  state: DraftState,
  stageOf: Readonly<Record<string, S>> | ((rec: RecordKey, path: string) => S | null | undefined),
): Partial<Record<S, number>> {
  const out: Partial<Record<S, number>> = {};
  for (const [rec, recEntries] of Object.entries(state.entries)) {
    for (const path of Object.keys(recEntries)) {
      const stage = typeof stageOf === 'function' ? stageOf(rec, path) : stageOf[path];
      if (stage) out[stage] = (out[stage] ?? 0) + 1;
    }
  }
  return out;
}

/** Renders history labels; injected by the caller, who knows the catalog. */
export interface DraftLabelFormatter {
  /** The field's label, e.g. "Date". */
  field: (rec: RecordKey, path: string) => string;
  /** One value as the ledger shows it, e.g. "1 Oct". */
  value: (rec: RecordKey, path: string, value: unknown) => string;
}

export const fallbackLabelFormatter: DraftLabelFormatter = {
  field: (_rec, path) => (isActionPath(path) ? path.slice(ACTION_PREFIX.length) : path),
  value: (_rec, _path, value) =>
    value === undefined || value === null || value === ''
      ? '—'
      : typeof value === 'string'
        ? value
        : JSON.stringify(value),
};

/** "Date 1 Oct → 2 Oct", "Discard 4 changes", "Keep mine: Email", … — what an undo or
 *  redo of `entry` changes (the UI adds the verb). */
export function describeHistoryEntry(
  entry: HistoryEntry,
  fmt: DraftLabelFormatter = fallbackLabelFormatter,
): string {
  const first = entry.items[0];
  if (!first) return '';
  const n = entry.items.length;
  switch (entry.kind) {
    case 'discard':
      return n === 1 ? 'Discard 1 change' : `Discard ${n} changes`;
    case 'keep_mine':
      return `Keep mine: ${fmt.field(first.rec, first.path)}`;
    case 'take_theirs':
      return `Take theirs: ${fmt.field(first.rec, first.path)}`;
    case 'edit': {
      if (entry.label) return entry.label;
      const before = first.prev ? first.prev.to : valueWithoutEntry(first.next!);
      const after = first.next ? first.next.to : valueWithoutEntry(first.prev!);
      const one = `${fmt.field(first.rec, first.path)} ${fmt.value(first.rec, first.path, before)} → ${fmt.value(first.rec, first.path, after)}`;
      return n === 1 ? one : `${one} (+${n - 1} more)`;
    }
  }
}

export interface UndoRedoInfo {
  canUndo: boolean;
  canRedo: boolean;
  /** What Undo would take back, e.g. "Date 1 Oct → 2 Oct"; null when nothing. */
  undoLabel: string | null;
  redoLabel: string | null;
}

export function undoRedoInfo(state: DraftState, fmt?: DraftLabelFormatter): UndoRedoInfo {
  const u = state.undoStack[state.undoStack.length - 1];
  const r = state.redoStack[state.redoStack.length - 1];
  return {
    canUndo: u !== undefined,
    canRedo: r !== undefined,
    undoLabel: u ? describeHistoryEntry(u, fmt) : null,
    redoLabel: r ? describeHistoryEntry(r, fmt) : null,
  };
}

// ---------------------------------------------------------------------------
// Compile
// ---------------------------------------------------------------------------

export interface DraftWireChange {
  entity: string;
  id: number;
  field?: string;
  action?: string;
  from: unknown;
  to: unknown;
}

/** Every entry as a wire change, deterministic (record, then path). Stale entries are
 *  included as they stand — the server reports their drift; Save is disabled meanwhile. */
export function compileChanges(state: DraftState): DraftWireChange[] {
  const out: DraftWireChange[] = [];
  for (const rec of sortedRecordKeys(state.entries)) {
    const { entity, id } = parseRecordKey(rec);
    const recEntries = state.entries[rec] ?? {};
    for (const path of Object.keys(recEntries).sort()) {
      const entry = recEntries[path]!;
      const target = isActionPath(path)
        ? { action: path.slice(ACTION_PREFIX.length) }
        : { field: path };
      out.push({ entity, id, ...target, from: entry.from ?? null, to: entry.to ?? null });
    }
  }
  return out;
}

/** Answers by key at `events.<key>` / `options.<key>` of the body — where a catalog
 *  option's `path` puts its answer (`events.update_events`, `options.user_email`). */
export type RecordChangesetAnswers = Readonly<Record<string, unknown>>;

/** The request body `compileChangeset` produces: the structural core of every record
 *  kind's changeset. Each record API narrows it to its endpoint's wire type
 *  (`toBookingChangeset`, `toOrderChangeset`); the server rejects what its kind does not
 *  take (`entity_not_allowed`). */
export interface RecordChangeset {
  description?: string | null;
  events?: RecordChangesetAnswers;
  options?: RecordChangesetAnswers;
  changes: DraftWireChange[];
}

export interface ChangesetExtras {
  description?: string | null;
  events?: RecordChangesetAnswers;
  options?: RecordChangesetAnswers;
}

function definedOnly<T extends object>(obj: T | undefined): T | undefined {
  if (!obj) return undefined;
  const out = Object.fromEntries(Object.entries(obj).filter(([, v]) => v !== undefined));
  return Object.keys(out).length > 0 ? (out as T) : undefined;
}

/** The request body: the compiled changes plus the ledger's description (trimmed,
 *  omitted when empty), event policy and choice answers (unset keys omitted). */
export function compileChangeset(state: DraftState, extras: ChangesetExtras = {}): RecordChangeset {
  const body: RecordChangeset = { changes: compileChanges(state) };
  const description = extras.description?.trim();
  if (description) body.description = description;
  const events = definedOnly(extras.events);
  if (events) body.events = events;
  const options = definedOnly(extras.options);
  if (options) body.options = options;
  return body;
}
